import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle2, Circle, Loader2, ArrowLeft, AlertTriangle, Calendar, Package, Factory } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const PRI_LABEL = { urgent: "Urgente", high: "Alta", normal: "Normale" };
const PRI_COLOR = { urgent: "#EF4444", high: "#F59E0B", normal: "#10B981" };

export default function TrackOrder() {
    const { token } = useParams();
    const navigate = useNavigate();
    const [job, setJob] = useState(null);
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(!!token);
    const [searchCode, setSearchCode] = useState("");

    useEffect(() => {
        if (!token) return;
        let cancelled = false;
        (async () => {
            try {
                setLoading(true);
                const { data } = await api.get(`/public/track/${token}`);
                if (!cancelled) { setJob(data); setErr(""); }
            } catch (e) {
                if (!cancelled) setErr(formatApiError(e));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [token]);

    const handleSearch = (e) => {
        e.preventDefault();
        const c = searchCode.trim();
        if (c) navigate(`/track/${c}`);
    };

    if (loading) {
        return (
            <div className="min-h-screen grid place-items-center" style={{ background: "var(--bg, #F7F8FB)" }}>
                <div className="flex items-center gap-2 mono text-[12px] uppercase tracking-wider" style={{ color: "var(--muted)" }}>
                    <Loader2 size={14} className="animate-spin" /> Caricamento...
                </div>
            </div>
        );
    }

    if (err || !job) {
        return (
            <div className="min-h-screen grid place-items-center p-6" style={{ background: "var(--bg, #F7F8FB)" }}>
                <div className="max-w-md w-full bg-white p-8 rounded-2xl text-center" style={{ border: "1px solid var(--line)" }} data-testid="track-error-box">
                    <AlertTriangle size={48} className="mx-auto mb-4" style={{ color: "#EF4444" }} />
                    <h1 className="text-[22px] font-extrabold mb-2">Ordine non trovato</h1>
                    <p className="text-[14px] mb-5" style={{ color: "var(--muted)" }}>Il codice di tracciamento non corrisponde a nessun ordine. Verifica di averlo digitato correttamente.</p>
                    <form onSubmit={handleSearch} className="flex gap-2 mb-4">
                        <input value={searchCode} onChange={(e) => setSearchCode(e.target.value)} placeholder="Inserisci codice corretto" className="input flex-1" data-testid="track-retry-input" />
                        <button type="submit" className="px-4 py-2 rounded-lg text-white font-bold text-[13px]" style={{ background: "#0F1F3D" }} data-testid="track-retry-btn">Cerca</button>
                    </form>
                    <Link to="/" className="text-[12.5px] font-bold inline-flex items-center gap-1" style={{ color: "#FF1A5C" }}>
                        <ArrowLeft size={13} /> Torna alla home
                    </Link>
                </div>
            </div>
        );
    }

    const stages = job.all_stages || [];
    const currentPos = job.current_stage?.position || 0;
    const isDelivered = job.current_stage?.kind === "delivered";

    return (
        <div className="min-h-screen" style={{ background: "var(--bg, #F7F8FB)" }}>
            <header className="px-6 md:px-12 py-5 flex items-center justify-between bg-white" style={{ borderBottom: "1px solid var(--line)" }}>
                <Link to={`/t/${job.tenant_slug}`} className="font-extrabold text-[16px]" style={{ color: "#0F1F3D" }} data-testid="track-back-link">
                    ← {job.tenant_name}
                </Link>
                <span className="mono text-[11px] uppercase tracking-wider" style={{ color: "var(--muted)" }}>Tracciamento ordine</span>
            </header>

            <main className="max-w-3xl mx-auto px-6 md:px-12 py-10">
                {/* Header card */}
                <div className="bg-white rounded-2xl p-6 md:p-8 mb-6" style={{ border: "1px solid var(--line)" }} data-testid="job-summary-card">
                    <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
                        <div>
                            <div className="mono text-[10px] uppercase tracking-wider font-bold mb-1" style={{ color: "var(--muted)" }}>Numero ordine</div>
                            <div className="font-extrabold text-[28px] mono" style={{ color: "#0F1F3D" }} data-testid="track-job-number">#{job.job_number}</div>
                        </div>
                        {job.priority && (
                            <span className="px-3 py-1 rounded-full text-[11px] font-extrabold text-white" style={{ background: PRI_COLOR[job.priority] || PRI_COLOR.normal }} data-testid="track-priority-badge">
                                {PRI_LABEL[job.priority] || "Normale"}
                            </span>
                        )}
                    </div>
                    <h1 className="text-[20px] md:text-[24px] font-extrabold leading-tight mb-4" style={{ color: "#0F1F3D" }} data-testid="track-job-title">{job.title}</h1>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-[13px]">
                        {job.department && (
                            <div className="flex items-center gap-2">
                                <Factory size={14} style={{ color: "var(--muted)" }} />
                                <span><span style={{ color: "var(--muted)" }}>Reparto: </span><strong>{job.department}</strong></span>
                            </div>
                        )}
                        {job.quantity != null && (
                            <div className="flex items-center gap-2">
                                <Package size={14} style={{ color: "var(--muted)" }} />
                                <span><span style={{ color: "var(--muted)" }}>Quantità: </span><strong>{job.quantity}</strong></span>
                            </div>
                        )}
                        {job.deadline && (
                            <div className="flex items-center gap-2">
                                <Calendar size={14} style={{ color: "var(--muted)" }} />
                                <span><span style={{ color: "var(--muted)" }}>Scadenza: </span><strong>{job.deadline}</strong></span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Stage tracker */}
                <div className="bg-white rounded-2xl p-6 md:p-8" style={{ border: "1px solid var(--line)" }} data-testid="track-stages-card">
                    <h2 className="font-extrabold text-[18px] mb-6" style={{ color: "#0F1F3D" }}>Stato del lavoro</h2>
                    <div className="space-y-1">
                        {stages.map((stage, idx) => {
                            const isCurrent = job.current_stage?.id === stage.id;
                            const isCompleted = currentPos > stage.position;
                            const isFuture = !isCurrent && !isCompleted;
                            return (
                                <div key={stage.id} className="flex items-stretch gap-4" data-testid={`stage-row-${idx}`}>
                                    <div className="flex flex-col items-center">
                                        <div className="w-9 h-9 rounded-full grid place-items-center transition" style={{
                                            background: isCurrent ? stage.color : (isCompleted ? "#10B981" : "#E5E7EB"),
                                            color: isFuture ? "#9CA3AF" : "#fff",
                                            boxShadow: isCurrent ? `0 0 0 4px ${stage.color}33` : "none",
                                        }}>
                                            {isCompleted ? <CheckCircle2 size={18} /> : (isCurrent ? <Loader2 size={16} className="animate-spin" /> : <Circle size={14} />)}
                                        </div>
                                        {idx < stages.length - 1 && (
                                            <div className="w-0.5 flex-1 my-1" style={{ background: isCompleted ? "#10B981" : "#E5E7EB", minHeight: "32px" }} />
                                        )}
                                    </div>
                                    <div className="flex-1 pb-6">
                                        <div className="font-extrabold text-[15px]" style={{ color: isFuture ? "#9CA3AF" : "#0F1F3D" }}>
                                            {stage.name}
                                        </div>
                                        {isCurrent && (
                                            <div className="text-[12.5px] mt-0.5 font-bold" style={{ color: stage.color }} data-testid="current-stage-label">
                                                ● Lavorazione in corso
                                            </div>
                                        )}
                                        {isCompleted && (
                                            <div className="text-[12px] mt-0.5" style={{ color: "var(--muted)" }}>Completato</div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    {isDelivered && (
                        <div className="mt-4 p-4 rounded-xl text-center" style={{ background: "#10B98115", color: "#065F46" }}>
                            <CheckCircle2 size={24} className="mx-auto mb-1" />
                            <strong className="text-[14px]">Ordine consegnato!</strong>
                        </div>
                    )}
                </div>

                <p className="text-center text-[11.5px] mt-6" style={{ color: "var(--muted)" }} data-testid="track-help-text">
                    Hai domande sul tuo ordine? Contatta direttamente <Link to={`/t/${job.tenant_slug}`} className="font-bold" style={{ color: "#FF1A5C" }}>{job.tenant_name}</Link>.
                </p>
            </main>
        </div>
    );
}
